import React, { FC, useEffect, useState } from "react";
import {
  ALL_MATTER_STATUSES,
  ALL_MATTER_TEMPLATE_STATUSES,
  MatterStatus,
  MatterType,
} from "../../data-types";
import { Dropdown, Menu, Tag, Tooltip } from "antd";
import { DownOutlined } from "@ant-design/icons";
import styles from "./matter-status-selector-dropdown.css";
import { MatterRecordType } from "../matter-table";
import { Spinner } from "../spinner";
import { LinkMatterModal } from "../link-matter-modal";

const {
  ["matter-selector"]: matterSelectorClassName,
  ["selector-trigger"]: selectorTrigger,
  ["selector-caret"]: selectorCaret,
} = styles as any;

const statusDisplayMap: Record<MatterStatus, { color: string; label: string }> = {
  [MatterStatus.Draft]: { color: "blue", label: "Draft" },
  [MatterStatus.ClientReview]: { color: "orange", label: "Client Review" },
  [MatterStatus.Live]: { color: "green", label: "Live" },
  [MatterStatus.Closed]: { color: "red", label: "Closed" },
  [MatterStatus.Lost]: { color: "red", label: "Lost" },
};
export const statusColor = (status: MatterStatus) => statusDisplayMap[status].color;
const statusLabel = (status: MatterStatus) => statusDisplayMap[status].label;

export type MatterStatusSelectorDropdownProps = {
  status?: MatterStatus;
  id: number;
  isParent: boolean | null;
  reference: string;
  onUpdate: (matter: Pick<MatterRecordType, "id" | "status" | "isParent">) => Promise<void>;
  externalMatterId: number | null;
  pmsName: string;
  updateAlert?: boolean;
  type?: MatterType;
  handleLoadingInParent?: boolean;
  allowLive?: boolean;
  tooltipMessage?: string;
};

export const MatterStatusSelectorDropdown: FC<MatterStatusSelectorDropdownProps> = ({
  status = MatterStatus.Draft,
  id,
  isParent,
  onUpdate,
  externalMatterId,
  pmsName,
  updateAlert = false,
  type: matterType = "matter",
  handleLoadingInParent,
  allowLive = true,
  tooltipMessage = "Live status is not available",
}) => {
  const matterStatuses =
    "matter" === matterType ? ALL_MATTER_STATUSES : ALL_MATTER_TEMPLATE_STATUSES;
  const [loading, setLoading] = useState<boolean>(false);
  const [showLinkMatterModal, setShowLinkMatterModal] = useState<boolean>(false);

  const [currentStatus, setCurrentStatus] = useState<MatterStatus>(status);

  // This has to be done https://vhudyma-blog.eu/react-antipatterns-props-in-initial-state/#:~:text=If%20you%20run,the%20initialValue%20prop.

  useEffect(() => {
    setCurrentStatus(status);
  }, [status]);

  const statusMenu = (
    <Menu
      selectedKeys={[currentStatus]}
      mode="vertical"
      onClick={async ({ key }) => {
        // If Live is disabled and the user somehow clicks it, do nothing
        if (key === MatterStatus.Live && !allowLive) {
          return;
        }

        if (key === MatterStatus.Live && !externalMatterId) {
          setShowLinkMatterModal(true);
          return;
        }
        setLoading(true);
        try {
          if (updateAlert) {
            await onUpdate({
              status: key as MatterStatus,
              ...(id && { id }),
            } as any);
          } else {
            await onUpdate({
              id,
              status: key as MatterStatus,
              isParent,
            });
          }

          setCurrentStatus(key as MatterStatus);
        } finally {
          setLoading(false);
        }
      }}
    >
      {matterStatuses.map((status) => {
        // For the Live status, render with tooltip when disabled
        if (status === MatterStatus.Live && !allowLive) {
          return (
            <Menu.Item key={status} disabled={true}>
              <Tooltip title={tooltipMessage} placement="right">
                <Tag color={statusColor(status)} style={{ opacity: 0.5, cursor: "not-allowed" }}>
                  {statusLabel(status)}
                </Tag>
              </Tooltip>
            </Menu.Item>
          );
        }

        return (
          <Menu.Item key={status}>
            <Tag color={statusColor(status)}>{statusLabel(status)}</Tag>
          </Menu.Item>
        );
      })}
    </Menu>
  );

  const DropDownContent = (
    <Dropdown disabled={loading === true} overlay={statusMenu}>
      <Tag color={statusColor(currentStatus)} className={selectorTrigger}>
        {" "}
        {statusLabel(currentStatus)} <DownOutlined className={selectorCaret} />
      </Tag>
    </Dropdown>
  );

  return (
    <>
      <div className={matterSelectorClassName}>
        {!handleLoadingInParent ? (
          <Spinner show={loading}>{DropDownContent}</Spinner>
        ) : (
          DropDownContent
        )}
      </div>

      <LinkMatterModal
        open={showLinkMatterModal}
        matterId={id}
        onClose={() => setShowLinkMatterModal(false)}
        pmsName={pmsName}
        onLinkAndConfirm={async () => {
          setLoading(true);
          try {
            await onUpdate({
              id,
              status: MatterStatus.Live,
              isParent,
            });
            setCurrentStatus(MatterStatus.Live);
          } finally {
            setLoading(false);
          }
        }}
      />
    </>
  );
};
